/* eslint-disable no-param-reassign */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Alert,
  Button,
  FormGroup,
  InputGroupText,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Modal as ReactModal,
} from 'reactstrap';
import {
  change,
  Field,
  Form,
  formValueSelector,
  reduxForm,
  touch,
} from 'redux-form';
import PropTypes from 'prop-types';
import SelectionableTable from '../../../common/forms/table/SelectionableTable';
import {
  CARGO_ITEM_STATUS,
  CREDIT_NOTE_MOTIVES,
  PEN_SYMBOL,
} from '../../../../config/constants';
import CargoItemStatusCell from '../../../common/cells/cargo/cargo-item/CargoItemStatusCell';
import FormItem from '../../../common/forms/FormItem';
import TextInput from '../../../common/forms/input/TextInput';
import { isRequired, validateMinNumber } from '../../../../utils/validators';
import { CURRENCY } from '../../../../config/locale';
import RefundParcelForm from './RefunParcelForm';
import Modal from '../../../common/modal/Modal';
import { enumToSelectOptions } from '../../../../utils/enum';
import Select from '../../../common/forms/select/Select';

const validateMin = validateMinNumber(0);

const CargoItemRefundModal = ({
  parcel,
  onSubmit,
  onCancel,
  dispatchChange,
  items,
  calculatedAmount,
  creditNoteMotiveValue,
  creditNoteMotiveDescription,
  dispatchTouch,
}) => {
  const [openConfirmationModal, setOpenConfirmationModal] =
    React.useState(false);
  const [openRefundModal, setOpenRefundModal] = React.useState(false);

  const ITEMS_REFUND_COLUMNS = [
    {
      Header: 'Correlativo',
      accessor: 'correlation',
      className: 'text-center',
    },
    {
      Header: 'Peso (kg)',
      accessor: 'weight',
      className: 'text-center',
    },
    {
      Header: 'Estado',
      accessor: 'status',
      className: 'text-center',
      Cell: (row) => <CargoItemStatusCell value={row.value} />,
    },
    {
      Header: 'Monto total',
      accessor: 'salePrice',
      className: 'text-center',
      Cell: (row) => `${PEN_SYMBOL} ${row.value.toFixed(2)}`,
    },
  ];

  const cargoItems = parcel.cargoItemList.filter(
    (item) => item.status === CARGO_ITEM_STATUS.RECEIVED.value || item.status === CARGO_ITEM_STATUS.IN_TEMPORAL_AGENCY.value,
  );

  const getMaxTotalAmountToRefund = () => {
    let amount = parcel.salePrice;
    if (parcel.creditNoteList && parcel.creditNoteList.length > 0) {
      parcel.creditNoteList.forEach((creditNote) => {
        if (creditNote.valid) {
          amount -= creditNote.amount;
        }
      });
    }

    return amount;
  };

  const getCurrentAmount = (itemIds) => {
    if (!itemIds || itemIds.length === 0) {
      return 0;
    }

    let amount = 0;
    cargoItems.forEach((item) => {
      if (itemIds.includes(item.id)) {
        amount += item.salePrice;
      }
    });
    return amount.toFixed(2);
  };

  useEffect(() => {
    if (items && items.length > 0) {
      const amount = getCurrentAmount(items);
      const maxAmount = getMaxTotalAmountToRefund();
      if (amount > maxAmount) {
        dispatchChange(
          'CargoItemRefundModal',
          'calculatedAmount',
          maxAmount.toFixed(2),
        );
        return;
      } else if (amount < 0) {
        dispatchChange('CargoItemRefundModal', 'calculatedAmount', 0);
        return;
      }

      dispatchChange('CargoItemRefundModal', 'calculatedAmount', amount);
    } else {
      dispatchChange('CargoItemRefundModal', 'calculatedAmount', 0);
    }
  }, [items]);

  const confirmRefundTypeModal = (
    <>
      <ReactModal
        isOpen={openConfirmationModal}
        toggle={() => setOpenConfirmationModal(false)}
        size="md"
      >
        <ModalHeader
          toggle={() => setOpenConfirmationModal(false)}
          close={
            <Button
              className="close"
              onClick={() => {
                setOpenConfirmationModal(false);
              }}
            >
              &times;
            </Button>
          }
        >
          Tipo de Devolución
        </ModalHeader>

        <ModalBody>
          Seleccionar el tipo de devolución que se desea realizar para el monto
          correspondiente.
          <FormGroup row className="mt-3">
            <FormItem label="Motivo de Generación" required>
              <Field
                name="creditNoteMotive"
                component={Select}
                options={enumToSelectOptions(CREDIT_NOTE_MOTIVES)}
                placeholder="Motivo de Generación"
                validate={[isRequired]}
              />
            </FormItem>
          </FormGroup>
          {creditNoteMotiveValue &&
            creditNoteMotiveValue.value ===
              CREDIT_NOTE_MOTIVES.OTHERS.value && (
              <FormGroup row>
                <FormItem label="Observación de Generación" required>
                  <Field
                    name="creditNoteMotiveDescription"
                    component={TextInput}
                    type="textarea"
                    placeholder="Observación de Generación"
                    validate={[isRequired]}
                  />
                </FormItem>
              </FormGroup>
            )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="warning"
            onClick={() => {
              dispatchTouch(
                'CargoItemRefundModal',
                'creditNoteMotive',
                'creditNoteMotiveDescription',
              );

              if (!creditNoteMotiveValue) return;

              if (
                creditNoteMotiveValue.value ===
                  CREDIT_NOTE_MOTIVES.OTHERS.value &&
                !creditNoteMotiveDescription
              )
                return;
              setOpenConfirmationModal(false);
              setOpenRefundModal(true);
            }}
          >
            <i className="fa fa-usd" /> Efectivo
          </Button>

          <Button
            type="button"
            onClick={() => {
              dispatchTouch(
                'CargoItemRefundModal',
                'creditNoteMotive',
                'creditNoteMotiveDescription',
              );

              if (!creditNoteMotiveValue) return;

              if (
                creditNoteMotiveValue.value ===
                  CREDIT_NOTE_MOTIVES.OTHERS.value &&
                !creditNoteMotiveDescription
              )
                return;
              setOpenConfirmationModal(false);
              onSubmit({
                itemIds: items,
                returnItemsToClient: false,
                amount: calculatedAmount,
                creditNoteMotive: creditNoteMotiveValue
                  ? creditNoteMotiveValue.value
                  : null,
                creditNoteMotiveDescription:
                  creditNoteMotiveDescription || null,
              });
            }}
            color="info"
          >
            <i className="fa fa-sticky-note" /> Nota de crédito
          </Button>
        </ModalFooter>
      </ReactModal>
    </>
  );

  return (
    <>
      <Form onSubmit={() => {}}>
        <FormGroup>
          <SelectionableTable
            columns={ITEMS_REFUND_COLUMNS}
            data={cargoItems}
            defaultPageSize={5}
            keyField="id"
            form="CargoItemRefundModal"
            showPagination
            returnOnlySelectedItems
          />{' '}
        </FormGroup>

        <Alert color="info">
          El monto máximo a devolver es{' '}
          <strong>S/ {getMaxTotalAmountToRefund().toFixed(2)}</strong>
        </Alert>

        <FormGroup row className="mt-4">
          <FormItem label="Devolver" required>
            <Field
              component={TextInput}
              type="number"
              name="calculatedAmount"
              append={<InputGroupText>{CURRENCY}</InputGroupText>}
              validate={[isRequired, validateMin]}
              disabled
            />
          </FormItem>
        </FormGroup>

        <div className="d-flex justify-content-end">
          <Button
            className="ml-1"
            color="primary"
            type="button"
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            className="ml-1"
            color="warning"
            type="button"
            onClick={() => setOpenConfirmationModal(true)}
            disabled={!items || items.length === 0 || calculatedAmount <= 0}
          >
            Devolver
          </Button>
        </div>
      </Form>

      {confirmRefundTypeModal}

      <Modal
        show={openRefundModal}
        title="Formulario para Devolución"
        body={
          <RefundParcelForm
            initialValues={{ amount: calculatedAmount }}
            onCancel={() => setOpenRefundModal(false)}
            onSubmit={(formValues) => {
              const newFormValues = {
                ...formValues,
                itemIds: items,
                returnItemsToClient: true,
                creditNoteMotive: creditNoteMotiveValue
                  ? creditNoteMotiveValue.value
                  : null,
                creditNoteMotiveDescription:
                  creditNoteMotiveDescription || null,
              };

              onSubmit(newFormValues);
            }}
          />
        }
        size="md"
      />
    </>
  );
};

CargoItemRefundModal.propTypes = {
  parcel: PropTypes.instanceOf(Object).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(PropTypes.number),
  calculatedAmount: PropTypes.number,
  creditNoteMotiveValue: PropTypes.instanceOf(Object),
  creditNoteMotiveDescription: PropTypes.string,
  dispatchTouch: PropTypes.func.isRequired,
};

CargoItemRefundModal.defaultProps = {
  items: [],
  calculatedAmount: 0,
  creditNoteMotiveValue: null,
  creditNoteMotiveDescription: null,
};

const mapDispatchToProps = {
  dispatchChange: change,
  dispatchTouch: touch,
};

const mapStateToProps = (state) => {
  const selector = formValueSelector('CargoItemRefundModal');

  return {
    items: selector(state, 'items'),
    calculatedAmount: selector(state, 'calculatedAmount'),

    creditNoteMotiveValue: selector(state, 'creditNoteMotive'),
    creditNoteMotiveDescription: selector(state, 'creditNoteMotiveDescription'),
  };
};

const formComponent = reduxForm({
  form: 'CargoItemRefundModal',
})(CargoItemRefundModal);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
